<template>
  <div>
    <wc-page-title :title='$store.state.icons.modules.services.title'></wc-page-title>
    <v-container>
      <v-row>
        <v-col cols='12'>
          <wc-data-box
            :title='currentDivision.division + " Service Items"'
            :showAdd='!currentDivision.is_all'
            @add='newService'
            showSearch
            showHelp
            :helpText='helpText'
            v-model='search'
          >
            <template v-slot:toolbarButtons>
              <wc-show-archived-button v-model='showArchived' @toggleShowArchived='showArchived = !showArchived'></wc-show-archived-button>
            </template>
            <wc-division-tabs v-model='currentDivision'> 
              <v-data-table
                  dense
                  :headers='headers'
                  :items='filteredServices'
                  :search='search'
                  :loading='isLoading'
                  sort-by='number'
                  sort-desc
                  @click:row='openItem'
              >
                <template v-slot:item.number='{ item }'>
                  <v-tooltip left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon  v-bind="attrs" v-on="on" x-small color='blue' v-if='item.is_estimate_only'>mdi-file-document-multiple-outline</v-icon>
                    </template>
                    <span>Estimate Only</span>
                  </v-tooltip>
                  {{ item.number }}
                </template>
                <template v-slot:item.division_obj.division='{ item }'>
                  <wc-division-nai :division_obj='item.division_obj'></wc-division-nai>
                </template>
                <template v-slot:item.description='{ item }'>
                  {{ item.description }}
                  <wc-archived v-if='item.archived'></wc-archived>
                </template>
                <template v-slot:item.service_price='{ item }'>
                  {{ item.service_price | decMoney }}
                </template>
                <template v-slot:item.company_cost='{ item }'>
                  {{ item.company_cost | decMoney }}
                </template>
              </v-data-table>
            </wc-division-tabs>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>

    <!-- add new dialog box below  -->
    <wc-dialog
      :showDialog='showDialog'
      :title='dialogTitle'
      :icon='currentDivision.icon'
      :iconColor='currentDivision.iconColor'
      @cancel='cancelEdit'
      @save='saveEdit'
      :loading='isLoading'
      :disableButtons='!isFormValid'
      lazy-validation
    >
      <v-form v-model="isFormValid" ref='form'>
        <v-select
          :items='divs'
          item-value='id'
          item-text='division'
          label='Division'
          v-model='newItem.division'
          @change='updateDivision'
        ></v-select>
        <v-text-field
          label='Description'
          ref='description'
          :rules='[v => !!v || "A description is required"]'
          v-model='newItem.description'
        ></v-text-field>
        <v-row>
          <v-col cols='12' md='4'>
            <v-text-field
              label='Estimate Price'
              :rules='[rules.decimalOrEmpty]'
              prepend-icon='mdi-currency-usd'
              reverse
              v-model='newItem.estimate_price'
            ></v-text-field>
          </v-col>
          <v-col cols='12' md='4'>
            <v-text-field
              label='Service Price'
              :rules='[rules.decimalOrEmpty]'
              prepend-icon='mdi-currency-usd'
              reverse
              v-model='newItem.service_price'
            ></v-text-field>
          </v-col>
          <v-col cols='12' md='4'>
            <v-text-field
              label='Company Cost'
              :rules='[rules.decimalOrEmpty]'
              prepend-icon='mdi-currency-usd'
              reverse
              v-model='newItem.company_cost'
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols='12' md='4'>
            <v-checkbox
              label='Estimate Only?'
              v-model='newItem.is_estimate_only'
            ></v-checkbox>
          </v-col>
          <v-col cols='12' md='4'>
            <v-text-field
              label='Number'
              v-model='newItem.number'
            ></v-text-field>
          </v-col>
          <v-col cols='12' md='4'>
            <v-text-field
              label='App Order'
              v-model='newItem.app_order'
            ></v-text-field>
          </v-col>
        </v-row>
        <br>
        <span>Notes</span>
        <v-textarea outlined auto-grow v-model='newItem.notes'></v-textarea>
      </v-form>
    </wc-dialog>
  </div>
</template>

<script>
  import assetsAPI from '@/modules/assets/api/services'
  import _ from 'lodash'
  import formRules from '@/mixins/formRules'

  export default {
    name: 'all-services',
    mixins: [formRules],
    data: () => ({
      currentDivision: {},
      resetItem: {
        description: "",
        estimate_price: null,
        service_price: null,
        company_cost: null,
        is_estimate_only: false,
        number: 0,
        app_order: 0,
        notes: "",
      },
      newItem: {},
      isLoading: true,
      showDialog: false,
      showArchived: false,
      isFormValid: true,
      search: "",
      services: [],
      headers: [
        { text: 'Number', value: 'number', width: '100' },
        { text: 'Division', value: 'division_obj.division', align: 'center' },
        { text: 'Description', value: 'description', },
        { text: 'Service Price', value: 'service_price', width:'150', align: 'right' },
        { text: 'Company Cost', value: 'company_cost', width:'150', align: 'right' }
      ],
      helpText: `These are the services that each division provides to customers at a given rate.
                For example, the snow division might offer a "Snow Clearing" service at a specific rate.
                The Lawn division might offer a "Lawn trimming" service, in addition to a "Lawn Cut" service,
                to customers.<br><br>These services will be "findable" when creating invoices, thus making it
                easier to add line items to an invoice by simply pulling up these data.`
    }),
    mounted () {
      this.getServices()
      this.itemReset()
      this.currentDivision = 'All' // eventually this can be set to a user defined value - use this.divs[n]
    },
    computed: {
      divisionedServices () {
        if (!this.currentDivision.is_all) {
          return this.services.filter(inv => inv.division === this.currentDivision.id)
        }
        else { return this.services }
      },
      filteredServices () {
        if (!this.showArchived) {
          return this.divisionedServices.filter(i => i.archived === false)
        }
          else { return this.divisionedServices }
      },
      dialogTitle () {
        return "Add a new " + this.currentDivision.division + " service"
      },
      divs () {
        return _.orderBy(this.$store.state.misc.divisions, 'order')
      },
    },
    methods: {
      updateDivision () {
        this.currentDivision = this.divs[_.findIndex(this.divs, ['id', this.newItem.division])]
      },
      async getServices () {
        this.isLoading = true
        let tmp = await assetsAPI.getServices()
        this.services = tmp.filter(s => s.is_labour === false)
        this.isLoading = false
      },
      newService () {
        this.showDialog = true
        this.itemReset()
        this.newItem.division = this.currentDivision.id
        setTimeout(() => {
          this.$refs.form.resetValidation()
        }, 50)
        setTimeout(() => {
          this.$refs.description.focus()
        }, 100)
      },
      cancelEdit () {
        this.showDialog = false
      },
      openItem (item) {
        this.$router.push({ name: 'ServiceDetailOverview', params: { id: item.id } })
      },
      async saveEdit () {
        this.isLoading = true
        this.services.push(await assetsAPI.newServiceItem(this.newItem))
        this.isLoading = false
        this.showDialog = false
      },
      itemReset() {
        this.newItem = _.cloneDeep(this.resetItem)
      }
    }
  }
</script>
